import { useEffect } from 'react'
import { getSocket } from '../services/socket'
import { useChannelStore } from '@/shell/stores/channelStore'
import { useServerStore } from '@/shell/stores/serverStore'
import { useVoiceStore } from '../stores/voiceStore'
import { useLivekitStore } from '../stores/livekitStore'
import { useAuthStore } from '../stores/authStore'
import { useToastStore } from '../stores/toastStore'
import { useTaskCountsStore, scheduleTaskCountsRefetch } from '../stores/taskCountsStore'
import { useTaskInboxStore, scheduleTaskInboxRefetch } from '../stores/taskInboxStore'
import { channelExistsInServers } from '../shell/lib/channelLiveSync'
import { handleVoiceMoved } from './voiceMoved'

export function useSocket() {
  const { activeChannelId } = useChannelStore()
  const { activeServerId } = useServerStore()
  useAuthStore((s) => s.user?.id) // keep store subscription alive

  // OSPROD-191: Tasks NEW-ticket counts (sidebar badge + per-board counter) +
  // the assignee toast. Initial fetch on mount/reconnect; `counts:changed`
  // (pushed to every board member's user room) debounce-refetches; `assigned`
  // pops a bottom-right toast for the person the ticket was just assigned to.
  useEffect(() => {
    const socket = getSocket()
    void useTaskCountsStore.getState().fetch()
    void useTaskInboxStore.getState().fetch()
    const onConnect = () => {
      void useTaskCountsStore.getState().fetch()
      void useTaskInboxStore.getState().fetch()
    }
    const onCountsChanged = () => scheduleTaskCountsRefetch()
    const onAssigned = (p: { title?: string }) => {
      useToastStore.getState().showToast({
        id: `task-assigned-${Date.now()}`,
        type: 'message',
        title: 'Ticket assigned to you',
        body: typeof p?.title === 'string' && p.title ? p.title : 'A ticket was assigned to you.',
      })
      scheduleTaskCountsRefetch()
      // OSPROD-191: bump the personal Tasks badge immediately, then reconcile.
      useTaskInboxStore.setState((s) => ({ unread: s.unread + 1 }))
      scheduleTaskInboxRefetch()
    }
    // OSPROD-191: any incoming task-category notification (assigned / reply /
    // mention / new ticket on my board) feeds the personal Tasks badge; any
    // read-state change elsewhere reconciles it.
    const onNotificationNew = (n: { category?: string }) => {
      const cat = n?.category ?? ''
      if (cat.startsWith('item.') || cat.startsWith('board.')) scheduleTaskInboxRefetch()
    }
    const onNotificationUpdated = () => scheduleTaskInboxRefetch()
    socket.on('connect', onConnect)
    socket.on('taskboard:counts:changed', onCountsChanged)
    socket.on('taskboard:assigned', onAssigned)
    socket.on('notification:new', onNotificationNew)
    socket.on('notification:updated', onNotificationUpdated)
    return () => {
      socket.off('connect', onConnect)
      socket.off('taskboard:counts:changed', onCountsChanged)
      socket.off('taskboard:assigned', onAssigned)
      socket.off('notification:new', onNotificationNew)
      socket.off('notification:updated', onNotificationUpdated)
    }
  }, [])

  useEffect(() => {
    const socket = getSocket()

    const onConnect = () => {
      // Rejoin all rooms after reconnect so we don't miss broadcasts
      const { activeServerId } = useServerStore.getState()
      const { activeChannelId } = useChannelStore.getState()
      const { currentChannelId } = useVoiceStore.getState()
      if (activeServerId) {
        socket.emit('server:join', { serverId: activeServerId })
      }
      if (activeChannelId) {
        if (activeChannelId.startsWith('dm-')) {
          socket.emit('dm:join', { dmChannelId: activeChannelId.replace('dm-', '') })
        } else {
          socket.emit('channel:join', { channelId: activeChannelId })
        }
      }
      if (currentChannelId) {
        socket.emit('voice:join', { channelId: currentChannelId })
      }
      // Converge the channel tree after any reconnect — we may have missed a
      // server:channels:updated broadcast while offline. Critical for the
      // Electron shell, which can't be reloaded by hand.
      useServerStore.getState().fetchServers().catch(() => {})
    }

    socket.on('connect', onConnect)
    return () => {
      socket.off('connect', onConnect)
    }
  }, [])

  useEffect(() => {
    if (!activeChannelId) return
    const socket = getSocket()
    if (activeChannelId.startsWith('dm-')) {
      socket.emit('dm:join', { dmChannelId: activeChannelId.replace('dm-', '') })
    } else {
      socket.emit('channel:join', { channelId: activeChannelId })
    }
    return () => {
      if (activeChannelId.startsWith('dm-')) {
        socket.emit('dm:leave', { dmChannelId: activeChannelId.replace('dm-', '') })
      } else {
        socket.emit('channel:leave', { channelId: activeChannelId })
      }
    }
  }, [activeChannelId])

  useEffect(() => {
    if (!activeServerId) return
    const socket = getSocket()
    socket.emit('server:join', { serverId: activeServerId })
    return () => {
      socket.emit('server:leave', { serverId: activeServerId })
    }
  }, [activeServerId])

  // Handle being kicked from a voice channel
  useEffect(() => {
    const socket = getSocket()
    const onKicked = ({ channelId }: { channelId: string; byUserId: string }) => {
      const { currentChannelId } = useVoiceStore.getState()
      if (currentChannelId === channelId) {
        useLivekitStore.getState().disconnect()
        useVoiceStore.getState().leaveChannel()
      }
    }
    socket.on('voice:kicked', onKicked)
    return () => { socket.off('voice:kicked', onKicked) }
  }, [])

  // Single active device: another of this user's devices/tabs just took over
  // the voice connection, so this one must yield (newest wins). Tear down our
  // LiveKit room and leave locally; leaveChannel() emits voice:leave, and the
  // server's leave is socket-count-aware so it won't evict the device that
  // took over. Surface a toast so the drop isn't a silent mystery.
  useEffect(() => {
    const socket = getSocket()
    const onElsewhere = () => {
      if (!useVoiceStore.getState().currentChannelId) return
      useLivekitStore.getState().disconnect()
      useVoiceStore.getState().leaveChannel()
      useToastStore.getState().showToast({
        id: `voice-elsewhere-${Date.now()}`,
        type: 'message',
        title: 'Disconnected from voice',
        body: 'You joined a voice channel on another device.',
      })
    }
    socket.on('voice:disconnected-elsewhere', onElsewhere)
    return () => { socket.off('voice:disconnected-elsewhere', onElsewhere) }
  }, [])

  // Handle being banned from a voice channel (live, mid-call).
  useEffect(() => {
    const socket = getSocket()
    const onBanned = ({ channelId, reason, expiresAt }: { channelId: string; reason: string | null; expiresAt: string | null; byUserId: string }) => {
      const { currentChannelId } = useVoiceStore.getState()
      useVoiceStore.getState().setBannedInfo({ channelId, reason, expiresAt })
      if (currentChannelId === channelId) {
        useLivekitStore.getState().disconnect()
        useVoiceStore.getState().leaveChannel()
      }
    }
    socket.on('voice:banned', onBanned)
    return () => { socket.off('voice:banned', onBanned) }
  }, [])

  // Handle being moved to another voice channel. Logic lives in the pure,
  // dependency-injected handleVoiceMoved (unit-tested in voiceMoved.test) —
  // see the comment there for why re-emitting voice:join matters.
  useEffect(() => {
    const socket = getSocket()
    const onMoved = (data: { fromChannelId: string; toChannelId: string; byUserId: string }) => {
      handleVoiceMoved(data, {
        getCurrentChannelId: () => useVoiceStore.getState().currentChannelId,
        getUsername: () => useAuthStore.getState().user?.username || 'User',
        disconnect: () => useLivekitStore.getState().disconnect(),
        setCurrentChannel: (toChannelId) =>
          useVoiceStore.setState({ currentChannelId: toChannelId, joinedAt: Date.now() }),
        emitJoin: (toChannelId) => socket.emit('voice:join', { channelId: toChannelId }),
        connect: (toChannelId, username) => useLivekitStore.getState().connect(toChannelId, username),
      })
    }
    socket.on('voice:moved', onMoved)
    return () => { socket.off('voice:moved', onMoved) }
  }, [])

  // Handle admin pin changes
  useEffect(() => {
    const socket = getSocket()
    const onPinChanged = ({ channelId, states }: { channelId: string; states: Array<{ userId: string; pinnedRole: string | null }> }) => {
      const { currentChannelId, setPinnedIds } = useVoiceStore.getState()
      if (currentChannelId !== channelId) return
      const primary = states.find((s) => s.pinnedRole === 'primary')?.userId ?? null
      const secondary = states.find((s) => s.pinnedRole === 'secondary')?.userId ?? null
      setPinnedIds(primary, secondary)
    }
    socket.on('voice:pin:changed', onPinChanged)
    return () => { socket.off('voice:pin:changed', onPinChanged) }
  }, [])

  // Lock/unlock badge in the channel sidebar. Server fans this out on
  // every channelApi.setLocked / first WAITING entry creation, scoped
  // to the channel's server (or space) room — so any member with that
  // server's tree loaded gets the live update without a refetch.
  useEffect(() => {
    const socket = getSocket()
    const onLocked = ({ channelId, isLocked }: { channelId: string; isLocked: boolean }) => {
      useServerStore.getState().setChannelLocked(channelId, isLocked)
    }
    socket.on('voice:channel:locked', onLocked)
    return () => { socket.off('voice:channel:locked', onLocked) }
  }, [])

  // Channel tree changed on a server (create / rename / move / permissions /
  // visibility / delete). The server pushes server:channels:updated to the
  // server room; we debounce, then re-fetch so the per-user visibility filter
  // re-runs authoritatively server-side. If the channel we're viewing
  // vanished (deleted or hidden), move off the now-dead view.
  useEffect(() => {
    const socket = getSocket()
    let timer: ReturnType<typeof setTimeout> | null = null
    const onChannelsUpdated = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        useServerStore.getState().fetchServers()
          .then(() => {
            const { activeChannelId } = useChannelStore.getState()
            if (activeChannelId && !activeChannelId.startsWith('dm-')) {
              if (!channelExistsInServers(useServerStore.getState().servers, activeChannelId)) {
                useChannelStore.getState().setActiveChannel(null)
              }
            }
          })
          .catch(() => {})
      }, 350)
    }
    socket.on('server:channels:updated', onChannelsUpdated)
    return () => {
      if (timer) clearTimeout(timer)
      socket.off('server:channels:updated', onChannelsUpdated)
    }
  }, [])

  // Lost access to / channel deleted out from under an active voice session.
  // Mirror voice:disconnected-elsewhere: tear down LiveKit, leave locally
  // (leaveChannel emits voice:leave so the server cleans up), and toast so
  // the drop isn't a silent mystery. The companion server:channels:updated
  // removes the channel from the sidebar in the same beat, so the user can't
  // immediately re-join.
  useEffect(() => {
    const socket = getSocket()
    const onAccessLost = ({ channelId, reason }: { channelId: string; reason: 'channel-deleted' | 'access-revoked' }) => {
      if (useVoiceStore.getState().currentChannelId !== channelId) return
      useLivekitStore.getState().disconnect()
      useVoiceStore.getState().leaveChannel()
      useToastStore.getState().showToast({
        id: `voice-access-lost-${Date.now()}`,
        type: 'message',
        title: reason === 'channel-deleted' ? 'Channel deleted' : 'Access changed',
        body: reason === 'channel-deleted'
          ? 'This voice channel was removed.'
          : 'You no longer have access to this voice channel.',
      })
    }
    socket.on('voice:access:lost', onAccessLost)
    return () => { socket.off('voice:access:lost', onAccessLost) }
  }, [])

  // Per-server module subscriptions: admin saved a change in the
  // /admin → Server modules drawer. Server emits to `server:<id>`
  // socket room (joined on `server:join` from the active server
  // switcher). We patch the matching server in-place so the
  // PrimarySidebar rail filter re-runs without a reload.
  useEffect(() => {
    const socket = getSocket()
    const onModulesUpdated = ({
      serverId,
      enabledModules,
    }: { serverId: string; enabledModules: Record<string, boolean> }) => {
      useServerStore.getState().applyModulesUpdate(serverId, enabledModules)
    }
    socket.on('server:modules:updated', onModulesUpdated)
    return () => { socket.off('server:modules:updated', onModulesUpdated) }
  }, [])

  // Probe remote-control takeover: another operator just took over a probe
  // control session we owned. The server emits `probe:control:revoked` to our
  // user-room (default namespace) so we learn the control ended without it being
  // a silent drop. Surface a toast naming who took over.
  useEffect(() => {
    const socket = getSocket()
    const onControlRevoked = ({ byName }: { deviceId?: string; byName?: string }) => {
      useToastStore.getState().showToast({
        id: `probe-control-revoked-${Date.now()}`,
        type: 'message',
        title: `${byName || 'Another operator'} took over remote control`,
      })
    }
    socket.on('probe:control:revoked', onControlRevoked)
    return () => { socket.off('probe:control:revoked', onControlRevoked) }
  }, [])
}
